import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslocoService } from '@ngneat/transloco';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConsentService } from '@core/consent/consent.service';
import { TenantService } from '@core/tenant/tenant.service';
import {
  DealerSite,
  DealerSitePageRef,
  DealerSiteProfile,
} from '@core/tenant/dealer-site.model';

/**
 * Barra de consent RGPD (CMP interno) — apenas sites dealer.
 * Browser-only: nunca renderiza no SSR (ConsentService.hasDecided() = false no servidor).
 */
@Component({
  selector: 'ade-cookie-consent',
  templateUrl: './cookie-consent.component.html',
  styleUrls: ['./cookie-consent.component.scss'],
})
export class AdeCookieConsentComponent implements OnInit, OnDestroy {
  isBrowser: boolean;
  visible = false;
  expanded = false;
  analytics = false;
  marketing = false;
  profile: DealerSiteProfile | null = null;

  private _unsub = new Subject<void>();

  constructor(
    @Inject(PLATFORM_ID) platformId: object,
    private _consent: ConsentService,
    private _tenant: TenantService,
    private _transloco: TranslocoService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    if (!this.isBrowser) return;
    this.visible = !this._consent.hasDecided();
    const cur = this._consent.current();
    if (cur) {
      this.analytics = cur.analytics;
      this.marketing = cur.marketing;
    }

    this._consent.openSubject.pipe(takeUntil(this._unsub)).subscribe(() => {
      const c = this._consent.current();
      this.analytics = c?.analytics ?? this.analytics;
      this.marketing = c?.marketing ?? this.marketing;
      this.expanded = true;
      this.visible = true;
    });

    this._tenant.profile$.pipe(takeUntil(this._unsub)).subscribe((p) => {
      this.profile = p;
    });
  }

  ngOnDestroy(): void {
    this._unsub.next();
    this._unsub.complete();
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
  }

  acceptAll(): void {
    this._consent.acceptAll();
    this._close();
  }
  rejectAll(): void {
    this._consent.rejectAll();
    this._close();
  }
  savePrefs(): void {
    this._consent.accept({
      analytics: this.analytics,
      marketing: this.marketing,
    });
    this._close();
  }

  private _close(): void {
    this.visible = false;
    this.expanded = false;
  }

  get ds(): DealerSite | undefined {
    return this.profile?.dealerSite;
  }

  private get _lang(): string {
    return this._transloco.getActiveLang()?.split('-')[0] || 'pt';
  }

  get privacyHref(): string | null {
    return this._legalLink(this.ds?.metaInfo?.privacyPolicyPage, [
      'politica-de-privacidade',
      'privacidade',
      'privacy-policy',
    ]);
  }

  get termsHref(): string | null {
    return this._legalLink(this.ds?.metaInfo?.termsPage, [
      'termos-e-condicoes',
      'termos',
      'terms',
    ]);
  }

  private _legalLink(
    page: DealerSitePageRef | undefined,
    slugCandidates: string[],
  ): string | null {
    const slug =
      page?.slug ??
      (this.profile?.pages || []).find((p) => slugCandidates.includes(p.slug))
        ?.slug;
    return slug ? `/${this._lang}/${slug}` : null;
  }
}
